/**
 * Customer Addenda Timeline — admin-only widget listing the GDPR Art. 16
 * rectification addenda generated for a specific customer.
 *
 * Fabrizio CR 23/08/2026 — Task residuo del blocco "Customer Self-Update
 * PDF Generation & Storage Flows" (Option C completo).
 *
 * Features:
 *  - Timeline verticale con chain of amendments (sequence_number)
 *  - Filtro per campo modificato (email / phone / tax_code / all)
 *  - Badge counter totale addenda
 *  - Empty state elegante
 *  - Download PDF: variante "unified" (contratto + addendum) o "addendum-only"
 *  - Export CSV per audit Legal/DPO
 *  - Tooltip GDPR Art. 16 sull'header
 *
 * Backend endpoints consumati:
 *   GET /api/admin/contract-addenda/customer/{customer_id}
 *   GET /api/admin/contract-addenda/customer/{customer_id}/export.csv
 *   GET /api/admin/contract-addenda/{addendum_id}/download?variant=unified|addendum
 */
import { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Download, Info, ChevronRight, Mail, Phone,
  IdCard, ShieldCheck, Loader2, FileSpreadsheet, ArrowRight,
} from "lucide-react";
import { API } from "../../config";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";

// Which fields can appear in an addendum (mirrors backend
// POST_SIGNATURE_LOCKED_FIELDS in services/contract_addendum_service.py).
const FIELD_META = {
  email:    { label: "Email",         Icon: Mail,   color: "text-lojack-blue-400", bg: "bg-lojack-blue-500/10", border: "border-lojack-blue-500/30" },
  phone:    { label: "Telefono",      Icon: Phone,  color: "text-purple-400",       bg: "bg-purple-500/10",       border: "border-purple-500/30" },
  tax_code: { label: "Codice Fiscale", Icon: IdCard, color: "text-lojack-clause-400", bg: "bg-lojack-clause-500/10", border: "border-lojack-clause-500/30" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
};

const fmtBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const getAuthHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};


// ─── Single row ─────────────────────────────────────────────────────────

const AddendumRow = ({ entry, onDownload, index }) => {
  const meta = FIELD_META[entry.field] || {
    label: entry.field, Icon: FileText,
    color: "text-gray-400", bg: "bg-gray-500/10", border: "border-gray-500/30",
  };
  const Icon = meta.Icon;

  return (
    <div
      className="relative pl-8 pb-5 last:pb-0"
      data-testid={`addendum-row-${index}`}
    >
      {/* Vertical timeline line */}
      <div className="absolute left-3 top-2 bottom-0 w-px bg-gray-800" />
      {/* Timeline dot */}
      <div className={`absolute left-0 top-1 w-6 h-6 rounded-full ${meta.bg} ${meta.border} border flex items-center justify-center`}>
        <Icon size={11} className={meta.color} />
      </div>

      <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3 hover:border-gray-700 transition-colors">
        {/* Header row */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Addendum</span>
            <span className="text-white text-sm font-bold">#{entry.sequence_number}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${meta.bg} ${meta.color} border ${meta.border}`}>
              <Icon size={10} /> {meta.label}
            </span>
          </div>
          <span className="text-gray-500 text-[11px] whitespace-nowrap">
            {fmtDateTime(entry.approved_at)}
          </span>
        </div>

        {/* Old → New value pill */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="px-2 py-1 rounded bg-red-500/10 border border-red-500/20 text-red-300 text-[11px] font-mono truncate max-w-[45%]" title={entry.old_value}>
            {entry.old_value || "—"}
          </span>
          <ArrowRight size={12} className="text-gray-600 shrink-0" />
          <span className="px-2 py-1 rounded bg-lojack-blue-500/10 border border-lojack-blue-500/20 text-lojack-blue-300 text-[11px] font-mono truncate max-w-[45%]" title={entry.new_value}>
            {entry.new_value || "—"}
          </span>
        </div>

        {/* Metadata mini-row */}
        <div className="flex items-center gap-3 text-[10px] text-gray-500 mb-3 flex-wrap">
          <span className="whitespace-nowrap">
            <span className="opacity-60">Contratto:</span>{" "}
            <span className="font-mono">{(entry.contract_id || "").slice(0, 8) || "—"}</span>
          </span>
          <span className="whitespace-nowrap">
            <span className="opacity-60">Dealer:</span>{" "}
            <span className="font-mono">{(entry.dealer_id || "").slice(0, 8) || "—"}</span>
          </span>
          <span className="whitespace-nowrap">
            <span className="opacity-60">Retention fino al:</span>{" "}
            {fmtDate(entry.retention_until)}
          </span>
        </div>

        {/* Download buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => onDownload(entry.id, "unified")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-lojack-red hover:bg-lojack-red/85 text-white text-[11px] font-semibold transition-colors"
            data-testid={`addendum-download-unified-${index}`}
            title="Contratto originale + addendum uniti (quello che ha ricevuto il cliente)"
          >
            <Download size={11} />
            PDF Unificato
            <span className="opacity-70 font-mono">{fmtBytes(entry.unified_size_bytes)}</span>
          </button>
          <button
            type="button"
            onClick={() => onDownload(entry.id, "addendum")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-200 text-[11px] font-semibold border border-gray-700 transition-colors"
            data-testid={`addendum-download-only-${index}`}
            title="Solo la pagina Addendum (per archivio Legal senza duplicare il contratto)"
          >
            <Download size={11} />
            Solo Addendum
            <span className="opacity-70 font-mono">{fmtBytes(entry.addendum_size_bytes)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

AddendumRow.propTypes = {
  entry: PropTypes.object.isRequired,
  onDownload: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};


// ─── Empty state ────────────────────────────────────────────────────────

const EmptyState = () => (
  <div
    className="text-center py-6 px-3 rounded-lg border border-dashed border-gray-800 bg-black/20"
    data-testid="addenda-empty-state"
  >
    <ShieldCheck size={28} className="text-gray-700 mx-auto mb-2" />
    <p className="text-gray-400 text-sm font-medium">Nessuna rettifica post-firma</p>
    <p className="text-gray-600 text-[11px] mt-1 max-w-xs mx-auto">
      Il cliente non ha ancora richiesto (o non è stata approvata) alcuna
      modifica di campi bloccati sul contratto firmato.
    </p>
  </div>
);


// ─── Main widget ────────────────────────────────────────────────────────

export const CustomerAddendaTimelineWidget = ({ customerId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!customerId) return;
      try {
        setLoading(true);
        setError(null);
        const r = await axios.get(
          `${API}/admin/contract-addenda/customer/${customerId}`,
          { headers: getAuthHeader() },
        );
        if (cancelled) return;
        setItems(r.data?.items || []);
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        console.error("addenda fetch failed", e);
        setError(detail || "Impossibile caricare gli addenda");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [customerId]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter(x => x.field === filter);
  }, [items, filter]);

  const totalCount = items.length;
  const fieldCounts = useMemo(() => {
    const counts = { email: 0, phone: 0, tax_code: 0 };
    items.forEach(x => { if (counts[x.field] !== undefined) counts[x.field] += 1; });
    return counts;
  }, [items]);

  const handleDownload = async (addendumId, variant) => {
    try {
      const r = await axios.get(
        `${API}/admin/contract-addenda/${addendumId}/download?variant=${variant}`,
        { headers: getAuthHeader(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Extract filename from Content-Disposition header (backend sets it).
      const cd = r.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      link.download = match ? match[1] : `LoJack_addendum_${variant}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Download ${variant === "unified" ? "PDF Unificato" : "Addendum"} avviato`);
    } catch (e) {
      console.error("addendum download failed", e);
      toast.error(e?.response?.data?.detail || "Download non riuscito");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExportingCsv(true);
      const r = await axios.get(
        `${API}/admin/contract-addenda/customer/${customerId}/export.csv`,
        { headers: getAuthHeader(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      link.download = match ? match[1] : `LoJack_addenda_customer_${customerId.slice(0, 12)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV avviato");
    } catch (e) {
      console.error("addenda csv export failed", e);
      toast.error(e?.response?.data?.detail || "Export CSV non riuscito");
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div
      className="bg-black/40 border border-gray-800 rounded-xl p-4"
      data-testid="customer-addenda-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-lojack-red/10 flex items-center justify-center">
            <FileText size={14} className="text-lojack-red" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white text-sm font-semibold">Rettifiche post-firma</h3>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                      data-testid="addenda-gdpr-tooltip-trigger"
                      aria-label="Informazioni GDPR Art. 16"
                    >
                      <Info size={12} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs bg-gray-900 border-gray-700 text-gray-200 text-xs">
                    <p className="font-semibold mb-1">GDPR Art. 16 — Diritto di rettifica</p>
                    <p className="opacity-80 leading-relaxed">
                      L&apos;interessato ha diritto alla rettifica di dati inesatti che lo
                      riguardano. Per campi bloccati del contratto firmato
                      (email/telefono/CF) il sistema genera un <em>addendum</em>
                      con doppio timestamp (richiesta cliente + approvazione dealer)
                      che integra il contratto originale senza invalidarne la firma.
                    </p>
                    <p className="opacity-60 mt-2 text-[10px]">
                      Retention: 10 anni · GDPR Art. 17 par. 3 lett. b
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">
              GDPR Art. 16 · Storia rettifiche
            </p>
          </div>
        </div>

        {/* Badge counter */}
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-lojack-red/10 border border-lojack-red/30 text-lojack-red text-[11px] font-bold"
            data-testid="addenda-total-badge"
          >
            {totalCount} {totalCount === 1 ? "addendum" : "addenda"}
          </span>
          {totalCount > 0 && (
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={exportingCsv}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 text-[11px] font-semibold border border-gray-700 transition-colors"
              data-testid="addenda-export-csv-button"
              title="Esporta l'elenco addenda in formato CSV per audit Legal/DPO"
            >
              {exportingCsv
                ? <Loader2 size={11} className="animate-spin" />
                : <FileSpreadsheet size={11} />}
              CSV
            </button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      {totalCount > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="addenda-filter-bar">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${
              filter === "all"
                ? "bg-white text-black"
                : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 border border-gray-700"
            }`}
            data-testid="addenda-filter-all"
          >
            Tutti · {totalCount}
          </button>
          {Object.entries(FIELD_META).map(([field, meta]) => {
            const c = fieldCounts[field] || 0;
            const active = filter === field;
            const disabled = c === 0;
            return (
              <button
                key={field}
                type="button"
                onClick={() => !disabled && setFilter(field)}
                disabled={disabled}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                  active
                    ? `${meta.bg} ${meta.color} border ${meta.border}`
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 border border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-800 disabled:hover:text-gray-400"
                }`}
                data-testid={`addenda-filter-${field}`}
              >
                <meta.Icon size={9} />
                {meta.label} · {c}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      {loading && (
        <div className="flex items-center justify-center py-8" data-testid="addenda-loading">
          <Loader2 size={20} className="animate-spin text-gray-500" />
        </div>
      )}

      {!loading && error && (
        <div
          className="text-center py-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs"
          data-testid="addenda-error"
        >
          {error}
        </div>
      )}

      {!loading && !error && totalCount === 0 && <EmptyState />}

      {!loading && !error && totalCount > 0 && filtered.length === 0 && (
        <div
          className="text-center py-4 rounded-lg border border-dashed border-gray-800 text-gray-500 text-xs"
          data-testid="addenda-filter-empty"
        >
          Nessun addendum per il filtro selezionato.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div data-testid="addenda-timeline">
          {filtered.map((entry, i) => (
            <AddendumRow
              key={entry.id}
              entry={entry}
              index={i}
              onDownload={handleDownload}
            />
          ))}
        </div>
      )}
    </div>
  );
};

CustomerAddendaTimelineWidget.propTypes = {
  customerId: PropTypes.string.isRequired,
};

export default CustomerAddendaTimelineWidget;
